import { accumulateMilestoneThresholds } from '~containers/road-to-rewards-configuration-page/components/MilestonesSection/coupon-groups';
import type { LoyaltyClubTierReward } from '~stores/loyalty-club';
import type {
    MilestoneOptionRow,
    MilestoneReachRow,
} from './use-campaign-milestone-choices';

/** Only the fields this module reads off a tier, so a fixture need not
 * fabricate an entire tier (same narrowing `rewards-inventory.ts` uses). */
export type MilestoneTierSkeleton = Pick<
    LoyaltyClubTierReward,
    'id' | 'position' | 'requiredPurchases' | 'rewardOptions'
>;

/** One coupon of a milestone, with its regional copies and its removed
 * options of the same name folded together. */
export interface MilestoneChoiceOption {
    key: string;
    label: string;
    choices: number;
    /** Percentage points of the bar, which is the whole milestone: this
     * option's share of the choices made, scaled down to the slice of those
     * who REACHED that chose at all. Equals `choices / reachedMembers` only
     * where the two axes agree on how many chose; see `buildMilestoneChoicePanels`. */
    sharePct: number;
}

export interface MilestoneChoicePanel {
    /** The tier's id, unique across the ladder where a threshold need not be. */
    key: string;
    /** Cumulative visit threshold, not the per-tier delta. */
    visits: number;
    /** `null` when the endpoint served no row for this milestone, which is not
     * the same as nobody having reached it. */
    reachedMembers: number | null;
    choosingMembers: number;
    awaitingMembers: number;
    /** The bar's remainder, so a full bar closes on 100. Left short on
     * purpose when the choice axis served nothing for a milestone that has
     * choosers: a bar drawn to 100 there would report an attribution gap as
     * if every reached member were still waiting. */
    awaitingPct: number;
    choiceBreakdown: MilestoneChoiceOption[];
}

/** Each option's share of the milestone's own choices, scaled down to the
 * slice of those who reached that chose at all, so the options fill exactly
 * `choosingPct` of the bar between them. */
function toChoiceBreakdown(
    byLabel: ReadonlyMap<string, { choices: number }>,
    choosingPct: number
): MilestoneChoiceOption[] {
    const total = [...byLabel.values()].reduce(
        (sum, entry) => sum + entry.choices,
        0
    );
    return [...byLabel].map(([label, entry]) => ({
        key: label,
        label,
        choices: entry.choices,
        sharePct: total > 0 ? (entry.choices / total) * choosingPct : 0,
    }));
}

/**
 * One panel per milestone of the ladder, in ladder order, each with the bar's
 * two denominators and the shares drawn from them.
 *
 * Options are folded by name WITHIN a milestone, which is what collapses a
 * coupon's regional copies into the one legend entry a reader expects (the
 * copies share their name and their UPC). Never across milestones: two tiers
 * can carry the same name over genuinely different coupons, as the ladder's
 * four "Placeholder (to be defined)" rows do, each on its own UPC.
 *
 * One denominator drives the bar: those who REACHED the milestone. The
 * options split the chooser slice between them by their own choice counts,
 * never by `choosingMembers` directly (the option rows are a partition of
 * their own sum, not of the chooser count), and Awaiting choice takes
 * whatever is left. So a bar closes on 100 without the two axes having to
 * agree on how many chose, at the price of an option's share reading as
 * `choices / reachedMembers` only where they do agree.
 */
export function buildMilestoneChoicePanels(
    tierRewards: readonly MilestoneTierSkeleton[],
    reachByMilestone: ReadonlyMap<string, MilestoneReachRow>,
    optionsByMilestone: ReadonlyMap<string, readonly MilestoneOptionRow[]>
): MilestoneChoicePanel[] {
    return accumulateMilestoneThresholds([...tierRewards])
        .map(({ tier, visits }) => ({
            tier,
            visits,
            options: optionsByMilestone.get(String(visits)) ?? [],
        }))
        .filter(
            ({ tier, options }) =>
                (tier.rewardOptions ?? []).length > 0 || options.length > 0
        )
        .map(({ tier, visits, options }) => {
            const byLabel = new Map<string, { choices: number }>();
            for (const option of options) {
                const entry = byLabel.get(option.label) ?? { choices: 0 };
                entry.choices += option.choices;
                byLabel.set(option.label, entry);
            }

            const reach = reachByMilestone.get(String(visits));
            const reachedMembers = reach?.reachedMembers ?? null;
            const choosingMembers = reach?.choosingMembers ?? 0;
            const hasReach = reachedMembers != null && reachedMembers > 0;
            const awaitingMembers =
                reachedMembers == null
                    ? 0
                    : Math.max(reachedMembers - choosingMembers, 0);

            // Capped: the choices axis reporting more choosers than the reach
            // axis reached would otherwise drive the remainder negative.
            const choosingPct = hasReach
                ? Math.min((choosingMembers / reachedMembers) * 100, 100)
                : 0;

            const choiceBreakdown = toChoiceBreakdown(byLabel, choosingPct);

            // `choosingPct` when the options drew nothing: taking their zero
            // literally would stretch Awaiting choice over the whole bar and
            // hide that the attribution, not the choosing, is what is missing.
            const optionsPct = choiceBreakdown.reduce(
                (sum, option) => sum + option.sharePct,
                0
            );
            const drawnPct = optionsPct > 0 ? optionsPct : choosingPct;

            return {
                key: String(tier.id),
                visits,
                reachedMembers,
                choosingMembers,
                awaitingMembers,
                awaitingPct: hasReach ? 100 - drawnPct : 0,
                choiceBreakdown,
            };
        });
}
